import { LuIcon, Menu, type MenuClickTriggerProps, type MenuRootProps, type MenuVariantProps } from "@overmap-ai/blocks"
import { type IssueComment, type Stored } from "@overmap-ai/common"
import { selectCurrentUser } from "@overmap-ai/core"
import { memo, type ReactElement, useCallback } from "react"

import { modelIconMapping } from "../../mappings/icons"
import { useAppSelector } from "../../store"

interface IssueCommentDropdownMenuProps
	extends MenuRootProps, Omit<MenuClickTriggerProps, "children">, MenuVariantProps {
	trigger: ReactElement
	issueComment: Stored<IssueComment>
	onResolvedChange: (issueComment: Stored<IssueComment>, resolved: boolean) => void
	onEdit: () => void
	onDelete: (issueComment: Stored<IssueComment>) => void
}

export const IssueCommentDropdownMenu = memo((props: IssueCommentDropdownMenuProps) => {
	const { trigger, disabled, issueComment, onResolvedChange, onEdit, onDelete, size, radius, variant, accentColor } =
		props
	const currentUser = useAppSelector(selectCurrentUser)

	const handleResolvedChange = useCallback(() => {
		onResolvedChange(issueComment, !issueComment.resolved)
	}, [issueComment, onResolvedChange])

	const handleDelete = useCallback(() => {
		onDelete(issueComment)
	}, [issueComment, onDelete])

	return (
		<Menu.Root>
			<Menu.ClickTrigger disabled={disabled}>{trigger}</Menu.ClickTrigger>
			<Menu.Content size={size} radius={radius} accentColor={accentColor} variant={variant}>
				<Menu.Group>
					<Menu.Item onSelect={handleResolvedChange}>
						<LuIcon icon={issueComment.resolved ? modelIconMapping.refresh : modelIconMapping.check} />
						{issueComment.resolved ? "Unresolve" : "Resolve"}
					</Menu.Item>
					{currentUser?.id === issueComment.created_by && (
						<>
							<Menu.Item onSelect={onEdit}>
								<LuIcon icon={modelIconMapping.edit} />
								Edit
							</Menu.Item>
							<Menu.Item onSelect={handleDelete}>
								<LuIcon icon={modelIconMapping.delete} />
								Delete
							</Menu.Item>
						</>
					)}
				</Menu.Group>
			</Menu.Content>
		</Menu.Root>
	)
})
IssueCommentDropdownMenu.displayName = "IssueCommentDropdownMenu"
